import { useEffect, useState } from "react";
import LoadingVideo from "../../../assets/loadingVideo/reportLoading.mp4";
import { FiMinimize2 } from "react-icons/fi";
import { motion, AnimatePresence } from "framer-motion";

const steps = [
  "Initiating Sandbox",
  "Searching in Databases",
  "Deploying AI Agents",
  "Searching Web Resources",
  "Generating Summary",
  "Compiling Report",
];

const LoadingOverlay = ({ onMinimize }: { onMinimize?: () => void }) => {
  const [elapsedTime, setElapsedTime] = useState(0);
  const [currentStep, setCurrentStep] = useState(0);
  useEffect(() => {
    const timer = setInterval(() => setElapsedTime((prev) => prev + 1), 1000);

    const stepInterval = setInterval(() => {
      setTimeout(() => {
        setCurrentStep((prev) => (prev + 1) % steps.length);
      }, 500);
    }, 3000);

    return () => {
      clearInterval(timer);
      clearInterval(stepInterval);
    };
  }, []);

  const formatTime = (seconds: number) => {
    const mins = String(Math.floor(seconds / 60)).padStart(2, "0");
    const secs = String(seconds % 60).padStart(2, "0");
    return `${mins}:${secs}`;
  };

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-white/10 backdrop-blur-sm px-4">
      {/* Video background */}
      {/* Video container with overlayed UI */}
      <div className="relative w-[80vw] max-w-7xl h-[60vh] sm:h-[70vh] md:h-[80vh] rounded-2xl overflow-hidden shadow-lg">
        {/* Video */}
        <video className="w-full h-full object-cover" autoPlay loop muted>
          <source src={LoadingVideo} type="video/mp4" />
        </video>

        {/* Timer */}
        <div className="absolute top-4 left-4 sm:top-6 sm:left-6 text-white text-sm sm:text-lg px-3 py-1 rounded-md bg-black/40 backdrop-blur">
          {formatTime(elapsedTime)}
        </div>

        {/* Minimize */}
        <button
          className="absolute top-4 right-4 sm:top-6 sm:right-6 text-xs sm:text-base bg-white text-gray-800 px-3 py-1 rounded-xl flex items-center gap-2 shadow hover:bg-gray-100 transition"
          onClick={onMinimize}
        >
          <FiMinimize2 />
          Minimize
        </button>
      </div>

      {/* Title */}
      <div className="absolute top-30 sm:top-25 w-full max-w-xl text-center px-4">
        <h2 className="text-xl sm:text-2xl text-blue-900 font-semibold">
          Creating Report...
        </h2>
        <p className="text-sm text-white opacity-90 mt-1 sm:mt-2">
          AI Agents may take up to 15 minutes to complete.
        </p>
      </div>

      {/* Step Bubble */}
      <AnimatePresence mode="popLayout">
        {[...Array(3)].map((_, i) => {
          const index = currentStep - (2 - i);
          if (index < 0) return null;

          const isLatest = i === 2;

          return (
            <motion.div
              key={index}
              className="absolute bg-white/30 text-black font-light text-lg sm:text-2xl max-w-[90vw] sm:max-w-[700px] px-6 py-2 rounded-full shadow-lg border border-gray-300"
              initial={{
                opacity: 0,
                y: isLatest ? 30 : -50 * (2 - i),
                scale: 0.95,
              }}
              animate={{
                opacity: isLatest ? 1 : 0.5,
                y: -60 * (2 - i),
                scale: 1 - 0.04 * (2 - i),
              }}
              exit={{
                opacity: 0,
                y: -60,
                scale: 0.9,
              }}
              transition={{ duration: 0.5 }}
              style={{ zIndex: i + 1 }}
            >
              {steps[index]}
            </motion.div>
          );
        })}
      </AnimatePresence>

      {/* Bottom Message */}
      <div className="absolute bottom-25 md:bottom-30 sm:bottom-25 text-[10px] sm:text-sm text-white opacity-80 text-center px-4">
        You can safely close this window. Report will be available after
        generation in the <strong>'Reports'</strong> section.
      </div>
    </div>
  );
};

export default LoadingOverlay;
